import { useState } from 'react'
import { ActionIcon, Button, Checkbox, Tooltip } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { showNotification } from '@mantine/notifications'
import { formatHumanName } from '@medplum/core'
import type { Patient } from '@medplum/fhirtypes'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'
import { COMBINED_PDF_MAX_RECORDS } from '@/model/middleware-types'
import { saveBlob } from '@/utils/downloadAttachment'

type BarProps = {
  patient: Patient
  resourceType: 'DiagnosticReport' | 'DocumentReference'
  /** Names the saved file: "<patient> - <fileLabel> - <date>.pdf". */
  fileLabel: string
  /** The loaded rows that can be ticked — what Select all selects. */
  includableIds: string[]
  selectedIds: ReadonlySet<string>
  onChange: (selectedIds: Set<string>) => void
}

/**
 * PD-1685 — Select all / count / Clear / Download as one PDF, above the Labs/Imaging or
 * Documents list.
 */
export function CombinedPdfSelectionBar({
  patient,
  resourceType,
  fileLabel,
  includableIds,
  selectedIds,
  onChange,
}: BarProps) {
  const client = useClient()
  const [downloading, setDownloading] = useState(false)
  const count = selectedIds.size
  const overLimit = count > COMBINED_PDF_MAX_RECORDS
  const allSelected = includableIds.length > 0 && includableIds.every((id) => selectedIds.has(id))

  const download = async () => {
    setDownloading(true)
    try {
      const file = await client.chartRecordsCombinedPdf({
        patientId: patient.id ?? '',
        resourceType,
        recordIds: [...selectedIds],
      })
      const name = formatHumanName(patient.name?.[0]) || 'Patient'
      saveBlob(file, `${name} - ${fileLabel} - ${new Date().toISOString().slice(0, 10)}.pdf`)
    } catch (err) {
      logError('patient', 'Combined PDF failed', err)
      showNotification({
        color: 'red',
        title: 'Download failed',
        message: err instanceof Error ? err.message : 'The PDF could not be created.',
      })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="mt-3 flex flex-col gap-2 text-[12px]">
      <div className="flex items-center gap-2">
        <Checkbox
          size="xs"
          label="Select all"
          checked={allSelected}
          indeterminate={count > 0 && !allSelected}
          disabled={includableIds.length === 0}
          onChange={(event) =>
            onChange(event.currentTarget.checked ? new Set(includableIds) : new Set())
          }
        />
        <span className="ml-auto text-[11px] whitespace-nowrap text-[#7D7987]">
          {count} selected
        </span>
      </div>
      {/* An icon, so the download label fits the narrow list rail. */}
      <div className="flex items-center gap-2">
        <Button
          size="compact-sm"
          className="flex-1"
          disabled={count === 0 || overLimit}
          loading={downloading}
          onClick={() => void download()}
        >
          Download as one PDF
        </Button>
        <Tooltip label="Clear selection">
          <ActionIcon
            variant="default"
            aria-label="Clear selection"
            disabled={count === 0}
            onClick={() => onChange(new Set())}
          >
            <IconX size={14} />
          </ActionIcon>
        </Tooltip>
      </div>
      {overLimit && (
        <span className="text-[11px] text-[#E5533D]">
          One PDF can hold up to {COMBINED_PDF_MAX_RECORDS} records.
        </span>
      )}
    </div>
  )
}

type CheckboxProps = Pick<BarProps, 'selectedIds' | 'onChange'> & {
  id: string
  title: string
  includable: boolean
}

/** The tick box on a row: it only selects — the row's own click (open the preview) never fires. */
export function RecordSelectCheckbox({
  id,
  title,
  includable,
  selectedIds,
  onChange,
}: CheckboxProps) {
  const toggle = (checked: boolean) => {
    const next = new Set(selectedIds)
    if (checked) next.add(id)
    else next.delete(id)
    onChange(next)
  }
  const box = (
    <Checkbox
      size="xs"
      aria-label={`Select ${title}`}
      checked={selectedIds.has(id)}
      disabled={!includable}
      onChange={(event) => toggle(event.currentTarget.checked)}
    />
  )
  return (
    // The wrapper stops the click even on a disabled box, and gives its tooltip the hover.
    <span className="mt-[2px] mr-2 inline-flex" onClick={(event) => event.stopPropagation()}>
      {includable ? box : <Tooltip label="No PDF or image to include">{box}</Tooltip>}
    </span>
  )
}
